import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { spaLogin as apiSpaLogin } from '../api/auth';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './OrganizerRegisterPage.module.css';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../config/mapTile';
import pinIconUrl from '../assets/pin.svg';
import { useTranslation } from 'react-i18next';

const venuePinIcon = L.icon({
  iconUrl: pinIconUrl,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const VenueMarker = ({ setLatLng }) => {
  useMapEvents({
    click(e) {
      setLatLng(e.latlng);
    },
  });
  return null;
};

const OrganizerRegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('organizerRegister');

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: '',
    venue_name: '',
    address: '',
    organization_name: '',
    slug: '',
    // website削除
  });

  const [latLng, setLatLng] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [agreedDocIds, setAgreedDocIds] = useState({});
  const [viewedDocIds, setViewedDocIds] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loadingRequiredDocs, setLoadingRequiredDocs] = useState(true);
  // チャージバック・返金費用同意チェックは不要

  useEffect(() => {
    const fetchRequiredDocs = async () => {
      setLoadingRequiredDocs(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/terms/required`, {
          params: { role: 'seller' },
        });
        const docs = Array.isArray(res.data?.documents) ? res.data.documents : [];
        setRequiredDocs(docs);
      } catch (error) {
        setFieldErrors((prev) => ({
          ...prev,
          agreements: [t('errorDocsLoad')],
        }));
      } finally {
        setLoadingRequiredDocs(false);
      }
    };

    fetchRequiredDocs();
  }, []);

  const getFieldError = (name) => fieldErrors?.[name]?.[0] || '';

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev?.[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const buildRegisterError = (err) => {
    const data = err?.response?.data;
    const normalizedFieldErrors = {};

    if (data?.errors && typeof data.errors === 'object') {
      Object.entries(data.errors).forEach(([key, value]) => {
        const messages = Array.isArray(value) ? value.map((msg) => String(msg)) : [String(value)];
        normalizedFieldErrors[key] = messages;
      });
    }

    if (data?.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors).flat().map((msg) => String(msg));
      if (messages.length > 0) {
        return {
          message: Array.from(new Set(messages)).join(' / '),
          fieldErrors: normalizedFieldErrors,
        };
      }
    }

    if (data?.message) {
      return {
        message: String(data.message),
        fieldErrors: normalizedFieldErrors,
      };
    }

    return {
      message: t('errorRegisterFailed'),
      fieldErrors: normalizedFieldErrors,
    };
  };

  const validateAndCheckSlug = async (rawSlug) => {
    const slug = rawSlug.trim().toLowerCase();
    if (!slug) return;

    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setFieldErrors((prev) => ({
        ...prev,
        slug: [t('errorSlugInvalid')],
      }));
      return;
    }

    clearFieldError('slug');

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/check-slug`, { params: { slug } });
      if (res.data.exists) {
        setFieldErrors((prev) => ({
          ...prev,
          slug: [t('errorSlugTaken')],
        }));
      }
    } catch (err) {
      // ignore network errors for now
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'role') {
      setForm((prev) => ({
        ...prev,
        role: value,
        venue_name: '',
        address: '',
        organization_name: '',
        slug: '',
      }));
      setLatLng(null);
      setFieldErrors({});
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);

    if (name === 'slug') {
      clearFieldError('slug');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    if (loadingRequiredDocs) {
      setFieldErrors({ agreements: [t('errorDocsLoading')] });
      return;
    }

    if (requiredDocs.length === 0) {
      setFieldErrors({ agreements: [t('errorDocsReload')] });
      return;
    }

    if (!form.role) {
      setFieldErrors({ role: [t('errorRoleRequired')] });
      return;
    }

    // 入力バリデーション
    if (form.role === 'artist' && !form.organization_name.trim()) {
      setFieldErrors({ organization_name: [t('errorArtistNameRequired')] });
      return;
    }

    if (form.role === 'venue' && !latLng) {
      setFieldErrors({ latitude: [t('errorMapRequired')] });
      return;
    }

    const missingDoc = requiredDocs.find((doc) => !agreedDocIds[doc.id]);
    if (missingDoc) {
      setFieldErrors({ agreements: [t('errorAgreementsRequired')] });
      return;
    }

    const data = {
      ...form,
      latitude: latLng?.lat,
      longitude: latLng?.lng,
      agreements: requiredDocs.map((doc) => ({ terms_version_id: doc.id })),
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/seller-register`, data);

      let resendVerificationToken = '';

      try {
        await apiSpaLogin({ email: form.email, password: form.password });
      } catch (loginError) {
        const responseData = loginError?.response?.data;
        if (responseData?.error_code === 'email_unverified') {
          resendVerificationToken = responseData?.resend_verification_token || '';
        }
      }

      navigate('/email-verification-notice', {
        state: {
          email: form.email,
          resendVerificationToken,
        },
      });
    } catch (err) {
      console.error('登録エラー:', err);
      const parsed = buildRegisterError(err);
      setFieldErrors(parsed.fieldErrors);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>{t('heading')}</h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        <label htmlFor="name">{t('labelName')}</label>
        <input id="name" type="text" name="name" placeholder={t('placeholderName')} value={form.name} onChange={handleChange} className={getFieldError('name') ? styles.inputError : ''} required />
        {getFieldError('name') && <p className={styles.fieldError}>{getFieldError('name')}</p>}
        <div style={{ height: 8 }} />
        <label htmlFor="email">{t('labelEmail')}</label>
        <input id="email" type="email" name="email" placeholder="email@example.com" autoComplete="username" value={form.email} onChange={handleChange} className={getFieldError('email') ? styles.inputError : ''} required />
        {getFieldError('email') && <p className={styles.fieldError}>{getFieldError('email')}</p>}
        <div style={{ height: 8 }} />
        <label htmlFor="password">{t('labelPassword')}</label>
        <input id="password" type="password" name="password" placeholder={t('placeholderPassword')} autoComplete="new-password" value={form.password} onChange={handleChange} className={getFieldError('password') ? styles.inputError : ''} required />
        {getFieldError('password') && <p className={styles.fieldError}>{getFieldError('password')}</p>}
        <div style={{ height: 8 }} />
        <input id="password_confirmation" type="password" name="password_confirmation" placeholder={t('placeholderPasswordConfirm')} autoComplete="new-password" value={form.password_confirmation} onChange={handleChange} className={getFieldError('password_confirmation') ? styles.inputError : ''} required />
        {getFieldError('password_confirmation') && <p className={styles.fieldError}>{getFieldError('password_confirmation')}</p>}
        <div style={{ height: 8 }} />

        <label htmlFor="role" style={{ fontWeight: 'bold', marginBottom: 4 }}>{t('labelRole')}</label>
        <div style={{ marginBottom: 8 }}>
          <select id="role" name="role" value={form.role} onChange={handleChange} className={getFieldError('role') ? styles.inputError : ''} required>
            <option value="">{t('roleDefault')}</option>
            <option value="organizer">{t('roleOrganizer')}</option>
            <option value="artist">{t('roleArtist')}</option>
            <option value="venue">{t('roleVenue')}</option>
          </select>
          {getFieldError('role') && <p className={styles.fieldError}>{getFieldError('role')}</p>}
        </div>

        {form.role === 'organizer' && (
          <>
            <label htmlFor="organization_name_organizer">{t('labelOrgName')}</label>
            <input
              id="organization_name_organizer"
              type="text"
              name="organization_name"
              placeholder={t('placeholderOrgName')}
              value={form.organization_name}
              onChange={handleChange}
              className={getFieldError('organization_name') ? styles.inputError : ''}
              required
            />
            {getFieldError('organization_name') && <p className={styles.fieldError}>{getFieldError('organization_name')}</p>}
            <div style={{ height: 8 }} />
            <div style={{ marginBottom: 8 }}>
              <label htmlFor="slug_organizer" style={{ fontWeight: 'bold' }}>{t('labelSlug')}</label>
              <input
                id="slug_organizer"
                type="text"
                name="slug"
                placeholder={t('placeholderSlugOrganizer')}
                value={form.slug}
                onChange={handleChange}
                onBlur={(e) => validateAndCheckSlug(e.target.value)}
                className={getFieldError('slug') ? styles.inputError : ''}
                required
              />
              {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
              <div style={{ fontSize: 12, color: '#666' }}>{t('slugNote')}<code>{`${process.env.REACT_APP_SITE_URL}/profile/acme-live`}</code></div>
            </div>
            <div style={{ height: 8 }} />
          </>
        )}

        {form.role === 'artist' && (
          <>
            <label htmlFor="organization_name_artist">{t('labelArtistName')}</label>
            <input
              id="organization_name_artist"
              type="text"
              name="organization_name"
              placeholder={t('placeholderArtistName')}
              value={form.organization_name}
              onChange={handleChange}
              className={getFieldError('organization_name') ? styles.inputError : ''}
              required
            />
            {getFieldError('organization_name') && <p className={styles.fieldError}>{getFieldError('organization_name')}</p>}
            <div style={{ height: 8 }} />
            <div style={{ marginBottom: 8 }}>
              <label htmlFor="slug_artist" style={{ fontWeight: 'bold' }}>{t('labelSlug')}</label>
              <input
                id="slug_artist"
                type="text"
                name="slug"
                placeholder={t('placeholderSlugArtist')}
                value={form.slug}
                onChange={handleChange}
                onBlur={(e) => validateAndCheckSlug(e.target.value)}
                className={getFieldError('slug') ? styles.inputError : ''}
                required
              />
              {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
              <div style={{ fontSize: 12, color: '#666' }}>{t('slugNoteArtist')}<code>{`${process.env.REACT_APP_SITE_URL}/profile/artist-name`}</code></div>
            </div>
            <div style={{ height: 8 }} />
            <div style={{ height: 8 }} />
          </>
        )}

        {form.role === 'venue' && (
          <>
            <div>
              <div style={{ marginBottom: 8 }}>
                <label htmlFor="slug_venue" style={{ fontWeight: 'bold' }}>{t('labelSlug')}</label>
                <input
                  id="slug_venue"
                  type="text"
                  name="slug"
                  placeholder={t('placeholderSlugVenue')}
                  value={form.slug}
                  onChange={handleChange}
                    onBlur={(e) => validateAndCheckSlug(e.target.value)}
                    className={getFieldError('slug') ? styles.inputError : ''}
                  required
                />
                  {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
                <div style={{ fontSize: 12, color: '#666' }}>{t('slugNote')}<code>{`${process.env.REACT_APP_SITE_URL}/profile/venue-name`}</code></div>
              </div>
              <label htmlFor="venue_name">{t('labelVenueName')}</label>
                <input id="venue_name" type="text" name="venue_name" placeholder={t('placeholderVenueName')} value={form.venue_name} onChange={handleChange} className={getFieldError('venue_name') ? styles.inputError : ''} required />
                {getFieldError('venue_name') && <p className={styles.fieldError}>{getFieldError('venue_name')}</p>}
              <div style={{ height: 8 }} />
              <label htmlFor="address">{t('labelAddress')}</label>
                <input id="address" type="text" name="address" placeholder={t('placeholderAddress')} value={form.address} onChange={handleChange} className={getFieldError('address') ? styles.inputError : ''} required />
                {getFieldError('address') && <p className={styles.fieldError}>{getFieldError('address')}</p>}
              <div style={{ height: 8 }} />
              <label>{t('labelMap')}</label>
              <div style={{ marginBottom: 4, color: '#555', fontSize: 14 }}>{t('mapNote')}</div>
                <div className={getFieldError('latitude') ? styles.mapError : ''}>
                  <MapContainer center={[35.68, 139.76]} zoom={12} style={{ height: '300px', marginBottom: '10px' }}>
                    <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
                    <VenueMarker
                      setLatLng={(value) => {
                        setLatLng(value);
                        clearFieldError('latitude');
                      }}
                    />
                    {latLng && <Marker position={latLng} icon={venuePinIcon} />}
                  </MapContainer>
                </div>
                {getFieldError('latitude') && <p className={styles.fieldError}>{getFieldError('latitude')}</p>}
            </div>
            <div style={{ height: 8 }} />
          </>
        )}


        <p style={{ margin: 0, color: '#555', fontSize: 13 }}>{t('noteOnboarding')}</p>
        <div style={{ height: 8 }} />

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{t('agreementsTitle')}</div>
          {loadingRequiredDocs && <p style={{ margin: '0 0 8px', color: '#555' }}>{t('loadingDocs')}</p>}
          {requiredDocs.map((doc) => (
            <label key={doc.id} style={{ display: 'block', marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={!!agreedDocIds[doc.id]}
                disabled={!viewedDocIds[doc.id]}
                onChange={(e) => {
                  setAgreedDocIds((prev) => ({ ...prev, [doc.id]: e.target.checked }));
                  clearFieldError('agreements');
                }}
              />{' '}
              <button
                type="button"
                onClick={() => {
                  setViewedDocIds((prev) => ({ ...prev, [doc.id]: true }));
                  setPreviewDoc(doc);
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'blue',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                }}
              >
                {t('termsTitle.' + doc.type, { defaultValue: doc.title })}
              </button>
              {' '}{t('agreeLabel')}
            </label>
          ))}
          {requiredDocs.length > 0 && (
            <p style={{ margin: '4px 0 0', color: '#555', fontSize: 12 }}>{t('docsNote')}</p>
          )}
          {getFieldError('agreements') && <p className={styles.fieldError}>{getFieldError('agreements')}</p>}
        </div>
        <div style={{ height: 8 }} />

        <button type="submit" disabled={loadingRequiredDocs}>{t('btnSubmit')}</button>
      </form>

      {previewDoc && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1000,
          }}
          onClick={() => setPreviewDoc(null)}
        >
          <div
            style={{
              width: 'min(840px, 100%)',
              maxHeight: '80vh',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 12,
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px' }}>{t('termsTitle.' + previewDoc.type, { defaultValue: previewDoc.title })}</h3>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewDoc.content || '') }} />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <button type="button" onClick={() => setPreviewDoc(null)}>{t('btnClose')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerRegisterPage;
